import type { AmazzingFilterGroup, StoreListingResult } from '~~/shared/types/store'

const CATEGORY_FILTER_IDS = new Set(['category', 'categories', 'kategoria', 'kategorie', 'c'])
const CATEGORY_FILTER_NAMES = new Set(['category', 'categories', 'kategoria', 'kategorie', 'c'])
const CATEGORY_QUERY_KEYS = ['kategorie', 'c'] as const
const PAGINATION_QUERY_KEYS = ['page', 'p'] as const

interface MergeStableCategoryTreeInput {
  currentListing: StoreListingResult | null | undefined
  stableListing: StoreListingResult | null | undefined
  currentUrl?: string | null | undefined
}

function normalizeFilterName(value: string) {
  return value.trim().toLocaleLowerCase('pl-PL')
}

function isCategoryFilterGroup(filter: AmazzingFilterGroup) {
  return CATEGORY_FILTER_IDS.has(normalizeFilterName(filter.id)) || CATEGORY_FILTER_NAMES.has(normalizeFilterName(filter.name))
}

function findCategoryFilter(filters: AmazzingFilterGroup[]) {
  return filters.find(isCategoryFilterGroup)
}

function parseRelativeUrl(value: string) {
  return new URL(value || '/', 'https://lavmag.local')
}

function serializeRelativeUrl(url: URL) {
  const query = url.searchParams.toString()
  return `${url.pathname}${query ? `?${query}` : ''}${url.hash}`
}

function readCategoryQueryValues(currentUrl: string) {
  const url = parseRelativeUrl(currentUrl)
  return new Set(CATEGORY_QUERY_KEYS.flatMap(key => url.searchParams.getAll(key)))
}

function normalizePathname(value: string) {
  const pathname = parseRelativeUrl(value).pathname
  return pathname === '/' ? pathname : pathname.replace(/\/+$/, '')
}

function readDirectCategoryPathId(value: string) {
  const lastSegment = normalizePathname(value).split('/').filter(Boolean).at(-1)
  const match = lastSegment?.match(/^(\d+)-/)
  return match?.[1] ?? null
}

function hasCategoryQuery(value: string) {
  const url = parseRelativeUrl(value)
  return CATEGORY_QUERY_KEYS.some(key => url.searchParams.has(key))
}

function isDirectCategoryPath(value: string) {
  return !hasCategoryQuery(value) && readDirectCategoryPathId(value) !== null
}

function readTargetCategory(valueUrl: string, fallback: string) {
  const url = parseRelativeUrl(valueUrl)

  for (const key of CATEGORY_QUERY_KEYS) {
    const queryValue = url.searchParams.get(key)
    if (queryValue) {
      return { key, value: queryValue }
    }
  }

  return { key: 'kategorie' as const, value: fallback }
}

function isNumericCategoryId(value: string) {
  return /^\d+$/.test(value.trim())
}

function readCategoryQuerySlug(valueUrl: string) {
  const url = parseRelativeUrl(valueUrl)

  for (const key of CATEGORY_QUERY_KEYS) {
    const queryValue = url.searchParams.get(key)?.trim()
    if (queryValue && !isNumericCategoryId(queryValue)) {
      return queryValue
    }
  }

  return null
}

function buildDirectCategoryUrl(value: AmazzingFilterGroup['values'][number]) {
  if (!isNumericCategoryId(value.id)) return null

  const querySlug = readCategoryQuerySlug(value.targetUrl)
  if (!querySlug) return null

  return `/${value.id}-${encodeURIComponent(querySlug)}`
}

function buildCategoryUrl(currentUrl: string, category: { key: (typeof CATEGORY_QUERY_KEYS)[number], value: string } | null) {
  const url = parseRelativeUrl(currentUrl)

  for (const key of CATEGORY_QUERY_KEYS) {
    url.searchParams.delete(key)
  }
  for (const key of PAGINATION_QUERY_KEYS) {
    url.searchParams.delete(key)
  }

  if (category) {
    url.searchParams.set(category.key, category.value)
  }

  return serializeRelativeUrl(url)
}

export function mergeStableCategoryTree(input: MergeStableCategoryTreeInput): StoreListingResult | null {
  const currentListing = input.currentListing
  const stableCategoryFilter = input.stableListing ? findCategoryFilter(input.stableListing.filters) : undefined
  const currentUrl = currentListing?.currentUrl ?? input.currentUrl

  if (!stableCategoryFilter) {
    if (currentListing) {
      return {
        ...currentListing,
        filters: currentListing.filters.filter(filter => !isCategoryFilterGroup(filter)),
      }
    }

    return input.stableListing ?? null
  }
  if (!currentUrl) return currentListing ?? input.stableListing ?? null

  const selectedCategoryIds = readCategoryQueryValues(currentUrl)
  const stableCategoryWithCurrentUrls = rewriteCategoryFilterUrls(stableCategoryFilter, currentUrl, {
    selectedIds: selectedCategoryIds,
    currentPath: normalizePathname(currentUrl),
    currentPathId: readDirectCategoryPathId(currentUrl),
  })

  if (!currentListing) {
    const stableNonCategoryFilters = input.stableListing?.filters.filter(filter => !isCategoryFilterGroup(filter)) ?? []
    const mergedFilters = [stableCategoryWithCurrentUrls, ...stableNonCategoryFilters]

    return input.stableListing
      ? {
          ...input.stableListing,
          currentUrl,
          filters: mergedFilters,
        }
      : null
  }

  const nonCategoryFilters = currentListing.filters.filter(filter => !isCategoryFilterGroup(filter))
  const mergedFilters = [stableCategoryWithCurrentUrls, ...nonCategoryFilters]

  return {
    ...currentListing,
    filters: mergedFilters,
  }
}

function rewriteCategoryFilterUrls(
  filter: AmazzingFilterGroup,
  currentUrl: string,
  selectedContext: { selectedIds: Set<string>, currentPath: string, currentPathId: string | null },
): AmazzingFilterGroup {
  return {
    ...filter,
    resetTargetUrl: buildCategoryUrl(currentUrl, null),
    values: filter.values.map(value => rewriteCategoryValueUrls(value, currentUrl, selectedContext)),
  }
}

function rewriteCategoryValueUrls<T extends AmazzingFilterGroup['values'][number]>(
  value: T,
  currentUrl: string,
  selectedContext: { selectedIds: Set<string>, currentPath: string, currentPathId: string | null },
): T {
  const targetCategory = readTargetCategory(value.targetUrl, value.id)
  const targetPath = normalizePathname(value.targetUrl)
  const hasDirectCategoryTarget = isDirectCategoryPath(value.targetUrl)
  const directCategoryUrl = hasDirectCategoryTarget ? value.targetUrl : buildDirectCategoryUrl(value)
  const selected = selectedContext.selectedIds.has(targetCategory.value)
    || selectedContext.selectedIds.has(value.id)
    || (selectedContext.currentPathId !== null && selectedContext.currentPathId === value.id)
    || (hasDirectCategoryTarget && selectedContext.currentPath === targetPath)
  const children = value.children?.map(child => rewriteCategoryValueUrls(child, currentUrl, selectedContext))

  return {
    ...value,
    selected,
    targetUrl: directCategoryUrl ?? buildCategoryUrl(currentUrl, selected ? null : targetCategory),
    ...(children ? { children } : {}),
  }
}
